import React, { FC, ReactElement, useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import MainLayout from "../Layout/MainLayout"
import StatusTabs from "../Components/BookingList/StatusTabs"
import Filters from "../Components/BookingList/Filters"
import BookingTable from "../Components/BookingList/BookingTable"
import Pagination from "../Components/BookingList/Pagination"
import { apiFunction } from "../../Helpers/apiHelper"

interface PreferredSlot {
  date: string
  timeSlot: string
  timeSlotLabel: string
  isFixed: boolean
}

interface Booking {
  id: string
  type: string
  contractorName: string
  address: string
  status: string
  statusColor: string
  preferredSlots: PreferredSlot[]
  appliedDatetime: string
  bookingId: number
}

interface FilterParams {
  dateFrom?: string
  dateTo?: string
  timeSlot?: string
}

const BookingList: FC = (): ReactElement => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [allBookings, setAllBookings] = useState<Booking[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  
  // URLパラメータからstatusを取得（架電リスト用）
  const urlStatus = searchParams.get('status')
  const initialStatus = urlStatus === 'callout' ? 'call_scheduled' : 'all'
  const [activeStatus, setActiveStatus] = useState(initialStatus)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    confirmed: 0,
    callScheduled: 0,
    cancelled: 0
  })
  
  // デフォルト期間を設定（今日から1ヶ月後）
  const getDefaultDateRange = () => {
    const today = new Date()
    const dateTo = new Date(today)
    dateTo.setDate(dateTo.getDate() + 30)
    
    // YYYY-MM-DD形式に変換（input type="date"用）
    const formatDate = (date: Date): string => {
      return date.toISOString().substring(0, 10)
    }
    
    return {
      dateFrom: formatDate(today),
      dateTo: formatDate(dateTo)
    }
  }
  
  const [filters, setFilters] = useState<FilterParams>(getDefaultDateRange())
  
  // URLパラメータが変更されたらactiveStatusを更新
  useEffect(() => {
    if (urlStatus === 'callout') {
      setActiveStatus('call_scheduled')
      setCurrentPage(1) // ステータス変更時は最初のページに戻す
    } else {
      setActiveStatus('all')
      setCurrentPage(1) // ステータス変更時は最初のページに戻す
    }
  }, [urlStatus])

  // フロントエンドのステータス名をバックエンドのステータス名に変換
  const convertStatusToBackend = (status: string): string | null => {
    const statusMap: Record<string, string> = {
      pending: 'unapproved',
      confirmed: 'approved',
      call_scheduled: 'callout',
      cancelled: 'canceled',
      all: ''
    }
    return status === 'all' ? null : statusMap[status] || null
  }

  // データを取得
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const params = new URLSearchParams()
        params.append('page', currentPage.toString())
        params.append('per_page', perPage.toString())
        
        if (searchQuery) {
          params.append('search', searchQuery)
        }
        
        // ステータスフィルター（activeStatusが'all'でない場合）
        const backendStatus = convertStatusToBackend(activeStatus)
        if (backendStatus) {
          params.append('status', backendStatus)
        }
        
        // フィルター
        if (filters.dateFrom) {
          params.append('date_from', filters.dateFrom)
        }
        if (filters.dateTo) {
          params.append('date_to', filters.dateTo)
        }
        if (filters.timeSlot) {
          params.append('time_slot', filters.timeSlot)
        }

        const data = await apiFunction<{
          data: Booking[]
          recordsTotal: number
          recordsFiltered: number
          statusCounts?: {
            all: number
            pending: number
            confirmed: number
            callScheduled: number
            cancelled: number
          }
        }>(`/api/bookings/tables?${params.toString()}`, {}, 'GET')

        if (data) {
          setAllBookings(data.data || [])
          setTotalCount(data.recordsTotal || 0)
          setStatusCounts(data.statusCounts || {
            all: 0,
            pending: 0,
            confirmed: 0,
            callScheduled: 0,
            cancelled: 0
          })
        }
      } catch (error) {
        console.error('Failed to fetch bookings:', error)
        setAllBookings([])
        setTotalCount(0)
        setStatusCounts({
          all: 0,
          pending: 0,
          confirmed: 0,
          callScheduled: 0,
          cancelled: 0
        })
      }
    }
    
    fetchBookings()
  }, [currentPage, perPage, searchQuery, filters, activeStatus])

  return (
    <MainLayout
      activeSidebarTab="all"
      sidebarTotalBookings={statusCounts.all}
      sidebarCallScheduled={statusCounts.callScheduled}
      headerTitle="予約申請一覧"
      headerBreadcrumb="ホーム / 予約管理 / 一覧"
      onSearch={setSearchQuery}
      onCreateNew={() => {
        // TODO: 新規予約作成処理
        console.log('新規予約作成')
      }}
      showSearch={true}
    >
      <StatusTabs
        activeStatus={activeStatus}
        counts={statusCounts}
        onStatusChange={(status) => {
          setActiveStatus(status)
          setCurrentPage(1) // ステータス変更時は最初のページに戻す
        }}
      />
      
      <Filters
        onFilterChange={(filterParams) => {
          setFilters(filterParams)
          setCurrentPage(1) // フィルター変更時は最初のページに戻す
        }}
      />
      
      <BookingTable
        bookings={allBookings}
        perPage={perPage}
        totalCount={totalCount}
        currentPage={currentPage}
        onPerPageChange={(value) => {
          setPerPage(value)
          setCurrentPage(1) // 表示件数変更時は最初のページに戻す
        }}
        onRowClick={(bookingId) => {
          navigate(`/bookings/${bookingId}`)
        }}
      />
      
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / perPage)}
        perPage={perPage}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
      />
    </MainLayout>
  )
}

export default BookingList

